/**
 * Centralized Status Icon Configuration
 *
 * App-wide source of truth for status icon mappings.
 * Covers all entity types: analyses, samples, team members, locations, connectors, etc.
 */

import {
  ArchiveX,
  CircleCheck,
  CircleMinus,
  CirclePause,
  CircleX,
  Clock,
  Mail,
  RefreshCw,
  Settings,
  Stamp,
  Trash2,
  TriangleAlert,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type HeroIcon = LucideIcon;

export interface StatusIconConfigItem {
  icon: HeroIcon;
  animate?: boolean;
}

export type StatusIconKey =
  // Analysis / sample statuses
  | "completed"
  | "success"
  | "pending"
  | "in_progress"
  | "processing"
  | "inference_complete"
  | "post_processing"
  | "failed"
  | "error"
  | "cancelled"
  | "scheduled"
  | "queued"
  | "waiting"
  | "draft"
  | "sign_off_requested"
  // Team / user statuses
  | "active"
  | "deleted"
  | "invited"
  | "accepted"
  | "approved"
  | "rejected"
  | "expired"
  | "sent"
  | "inactive"
  // Location / aggregate statuses
  | "in_use"
  | "in use"
  | "not_in_use"
  | "not in use"
  | "archived"
  // Completion variant
  | "processed"
  // Sync / connector statuses
  | "running"
  | "syncing";

export const STATUS_ICONS: Record<StatusIconKey, StatusIconConfigItem> = {
  // Success states — icons inherit badge text color via currentColor
  completed: { icon: CircleCheck },
  success: { icon: CircleCheck },
  active: { icon: CircleCheck },
  approved: { icon: CircleCheck },
  accepted: { icon: CircleCheck },
  in_use: { icon: CircleCheck },
  "in use": { icon: CircleCheck },

  processed: { icon: CircleCheck },

  // Pending / waiting states
  pending: { icon: Clock },
  queued: { icon: Clock },
  waiting: { icon: Clock },
  draft: { icon: CirclePause },

  // Processing states
  in_progress: { icon: Settings, animate: false },
  processing: { icon: Settings, animate: false },
  inference_complete: { icon: Settings, animate: false },
  post_processing: { icon: Settings, animate: false },
  running: { icon: RefreshCw, animate: false },
  syncing: { icon: RefreshCw, animate: false },

  // Error / negative states
  failed: { icon: TriangleAlert },
  error: { icon: CircleX },
  cancelled: { icon: CircleX },
  rejected: { icon: CircleX },
  expired: { icon: Clock },
  deleted: { icon: Trash2 },
  archived: { icon: ArchiveX },
  not_in_use: { icon: ArchiveX },
  "not in use": { icon: ArchiveX },
  inactive: { icon: CircleMinus },

  // Scheduled / invitation states
  scheduled: { icon: Zap },
  invited: { icon: Mail },
  sent: { icon: Mail },

  // Sign-off request state
  sign_off_requested: { icon: Stamp },
} as const;

export const DEFAULT_STATUS_ICON: StatusIconConfigItem = {
  icon: Clock,
};
